import React, { useState, useEffect, useRef } from 'react';
import './App.css';

function App() {
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [endTime, setEndTime] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [previousTime, setPreviousTime] = useState(0);
  const [inputTime, setInputTime] = useState('');
  const intervalRef = useRef(null);
  const channelRef = useRef(null);
  // for pie chart
  const [isReset, setIsReset] = useState(false);
  const [denominator, setDenominator] = useState();
  const [rate, setRate] = useState(0.0);

  useEffect(() => {
    const storedState = localStorage.getItem('timerState');
    if (storedState) {
      const { running, expectedEndTime, timeLeft, denominator } = JSON.parse(storedState);
      if (running) {
        const endTime = new Date(expectedEndTime);
        setEndTime(endTime);
        const now = new Date();
        if (endTime > now) {
          setIsRunning(true);
          const remainingTime = Math.max(0, Math.round((endTime - now) / 1000));
          setTime(remainingTime);
        }
      } else {
        setTime(timeLeft);
      }
      setDenominator(denominator);
    }

    channelRef.current = new BroadcastChannel('timer-channel');
    channelRef.current.onmessage = (event) => {
      if (event.data.type === 'timer-running') {
        setEndTime(new Date(event.data.endTime));
        setIsRunning(true);
      } else if (event.data.type === 'timer-expired') {
        setTime(0);
        setIsRunning(false);
        setEndTime(null);
      }
    };

    return () => {
      if (channelRef.current) {
        channelRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (isRunning && endTime) {
      intervalRef.current = setInterval(() => {
        const now = new Date();
        if (endTime > now) {
          const remainingTime = Math.max(0, Math.round((endTime - now) / 1000));
          setTime(remainingTime);
          setRate(remainingTime / denominator);
        } else {
          setTime(0);
          setIsRunning(false);
          setEndTime(null);
          setRate(0);
          localStorage.removeItem('timerEndTime');
          clearInterval(intervalRef.current);
        }
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning, endTime, denominator]);

  useEffect(() => {
    if (inputTime === '') {
      setTime(previousTime);
    } else {
      const input = parseInt(inputTime.replace(/[\D]/g, ''));
      const fakeMinutes = Math.floor(input / 100);
      const fakeSeconds = input % 100;
      setTime(Math.min(99 * 60 + 59, fakeMinutes * 60 + fakeSeconds));
      setRate(1);

      localStorage.setItem('timerState', JSON.stringify({
        running: false,
        expectedEndTime: null,
        timeLeft: time,
        denominator: time,
      }));
    }
  }, [inputTime, previousTime]);

  useEffect(() => {
    if (denominator) {
      const storedState = localStorage.getItem('timerState');
      if (storedState) {
        const { running, expectedEndTime, timeLeft } = JSON.parse(storedState);
        localStorage.setItem('timerState', JSON.stringify({
          running: running,
          expectedEndTime: expectedEndTime,
          timeLeft: timeLeft,
          denominator: denominator,
        }));
      }
    }
  }, [denominator]);

  const handleStart = () => {
    if (!isRunning) {
      const now = new Date();
      const newEndTime = new Date(now.getTime() + time * 1000);
      setEndTime(newEndTime);
      const { denominator } = JSON.parse(localStorage.getItem('timerState'));
      localStorage.setItem('timerState', JSON.stringify({
        running: true,
        expectedEndTime: newEndTime.toISOString(),
        timeLeft: time,
        denominator: isReset ? time : denominator,
      }));
      if (isReset) {
        setIsReset(false);
        setDenominator(time);
      }
      setIsRunning(true);
    }
  };

  const handleStop = () => {
    setIsRunning(false);
    clearInterval(intervalRef.current);
    const now = new Date();
    const remainingTime = Math.max(0, Math.round((endTime - now) / 1000));
    const { denominator } = JSON.parse(localStorage.getItem('timerState'));
    localStorage.setItem('timerState', JSON.stringify({
      running: false,
      expectedEndTime: null,
      timeLeft: remainingTime,
      denominator: denominator,
    }));
  };

  const handleReset = () => {
    setTime(0);
    setIsRunning(false);
    setEndTime(null);
    setIsReset(true);
    localStorage.setItem('timerState', JSON.stringify({
      running: false,
      expectedEndTime: null,
      timeLeft: 0,
      denominator: null,
    }));
    clearInterval(intervalRef.current);
  };

  const setAndStart = (min_s) => {
    handleReset();
    setInputTime(min_s);
  };

  const handleFocus = () => {
    setIsEditing(true);
    setPreviousTime(time);
    setInputTime('');
  };

  const handleBlur = () => {
    setIsReset(true);
    setIsEditing(false);
  };

  const handleKeyDown = (event) => {
    if (event.key >= '0' && event.key <= '9') {
      if (inputTime.length === 4) {
        setInputTime('9959');
      } else {
        setInputTime(inputTime + event.key);
      }
    } else if (event.key === 'Backspace' && inputTime.length > 0) {
      setInputTime(inputTime.slice(0, inputTime.length - 1));
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(remainingSeconds).padStart(2, '0');
    return `${formattedMinutes}:${formattedSeconds}`;
  };

  const formatEdittingTime = (inputSeconds) => {
    const seconds = parseInt(inputSeconds.replace(/[\D]/g, ''));
    if (isNaN(seconds)) {
      return '';
    }
    const minutes = Math.floor(seconds / 100);
    const remainingSeconds = seconds % 100;
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(remainingSeconds).padStart(2, '0');
    return `${formattedMinutes}:${formattedSeconds}`;
  };

  const PartialCircle = (x) => {
    const size = 300;
    const center = size / 2;
    const radius = center - 5;

    if (x < 0) {
      return null;
    }

    const angle = x * 360;
    const largeArcFlag = angle > 180 ? 1 : 0;
    const rad = (angle * Math.PI) / 180;
    const xCoord = center + radius * Math.sin(rad);
    const yCoord = center - radius * Math.cos(rad);

    const pathData = `
      M ${center} ${center}
      L ${center} ${center - radius}
      A ${radius} ${radius} 0 ${largeArcFlag} 1 ${xCoord} ${yCoord}
      Z
    `;

    return (
      <svg width={size} height={size}>
         <circle cx={center} cy={center} r={radius} fill="none" stroke="#aaa" stroke-width="2" />
        <path d={pathData} fill="white" />
      </svg>
    );
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="partial-circle">
          {PartialCircle(rate)}
        </div>
        <div className="header-content">
          <p>
            <input
              type="text"
              inputMode='numeric'
              value={isEditing ? formatEdittingTime(inputTime) : formatTime(time)}
              placeholder={formatTime(previousTime)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              onChange={() => {}}
            />
          </p>
          <div className='minute-button' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <button onClick={() => setAndStart('0500')}>5</button>
            <button onClick={() => setAndStart('1000')}>10</button>
            <button onClick={() => setAndStart('1500')}>15</button>
            <button onClick={() => setAndStart('3000')}>30</button>
          </div>
          <div className='state-button' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {!isRunning ? (
              <button onClick={handleStart}>Start</button>
            ) : (
              <button onClick={handleStop}>Stop</button>
            )}
            <button onClick={handleReset}>Reset</button>
          </div>
        </div>
      </header>
    </div>
  );
}

export default App;
